<template>
  <BasePopover
    v-model="open"
    popper-class="m-expanding-input__popper"
    offset="0px"
    :close-on-click-reference="false"
    :popper-id="`popper_${_id}`"
    :options="popperOptions"
    placement="bottom-start"
    :activate-delay="10"
  >
    <template slot="reference">
      <div
        class="m-expanding-input"
        :class="{
          'm-expanding-input_error': error,
          'm-expanding-input_disabled': disabled
        }"
        @click="clickOnRef"
      >
        <div
          :id="`expanding-input-wrapper-${_id}`"
          ref="inputWrapper"
          class="m-expanding-input__field-wrapper"
          :style="{height: `${inputHeight}px`}"
        >
          <span>{{ innerValue }}</span>
        </div>
      </div>
    </template>
    <template slot="popper">
      <div class="m-expanding-input__grow-wrap">
        <textarea
          :id="_id"
          :ref="`textarea${_id}`"
          :value="innerValue"
          :title="label || placeholder"
          :placeholder="placeholder"
          :disabled="disabled"
          @input="onInput($event.target.value)"
          @click="onClick"
        />
      </div>
    </template>
  </BasePopover>
</template>

<script>
  import BasePopover from 'components/common/BasePopover';
  import InputMixin from 'components/common/mixins/InputMixin';
  import nextFrame from 'utils/nextFrame';

  export default {
    name: 'MExpandingInput',
    components: {BasePopover},
    mixins: [InputMixin],
    props: {
      value: {
        type: [Number, String],
        default: ''
      },
      inputId: {
        type: String,
        default: ''
      }
    },
    data () {
      return {
        innerValue: this.value,
        open: false
      };
    },
    computed: {
      inputHeight () {
        return this.small ? 28 : this.large ? 32 : 28;
      },
      _id () {
        return this.inputId;
      },
      popperOptions () {
        return {
          modifiers: {
            preventOverflow: {
              priority: ['bottom-start']
            },

            // makes popper render under the reference
            flip: {
              boundariesElement: `#expanding-input-wrapper-${this._id}`
            }
          }
        };
      }
    },
    watch: {
      open () {
        if (this.open) {
          const expandingWrapper = this.$refs.inputWrapper;
          const textarea = this.$refs[`textarea${this._id}`];
          const popper = document.getElementById(`#popper_${this._id}`);

          nextFrame(() => {

            /*
             * kind of hack
             * renders popper right on the reference element
             */
            popper.style.top = `-${this.inputHeight}px`;

            // set width for m-expanding-input__grow-wrap div
            popper.childNodes[0].style.width = `${expandingWrapper.clientWidth + 2}px`;

            // 10px - it's a padding in textarea. Do not use border-box, because it can provoke jumping
            textarea.style.width = `${expandingWrapper.clientWidth - 10}px`;
            textarea.focus();
          });
        }

        if (!this.open) {
          this.$emit('input', this.innerValue);
        }
      }
    },
    methods: {
      onClick (e) {
        this.$emit('click', e.target);
      },
      onInput (value) {
        this.innerValue = value;
        const textarea = this.$refs[`textarea${this._id}`];

        /*
         * it prevents textarea jumping during the height changes while user is typing text
         * the best article about autogrowing textarea
         * https://css-tricks.com/the-cleanest-trick-for-autogrowing-textareas/
         */
        if (textarea) {
          textarea.parentNode.dataset.replicatedValue = value;
        }

        if (!this.emitInputOnChange) {
          this.$emit('input', value);
        }
      },
      clickOnRef () {
        this.$nextTick(() => {
          const textarea = this.$refs[`textarea${this._id}`];
          if (textarea) {

            // space is added to inner value to recalculate the height of textarea when it renders at first time
            nextFrame(() => {
              textarea.parentNode.dataset.replicatedValue = `${this.innerValue} `;
            });
          }
        });
      }
    }
  };
</script>

<style>
  :root {
    --expanding-textarea-font-size: 14px;
    --expanding-textarea-height: 28px;
    --expanding-textarea-padding: 5px;
  }

  .m-expanding-input {
    white-space: nowrap;
    word-break: break-all;
    word-wrap: break-word;
  }

  .m-expanding-input__field-wrapper {
    font-size: var(--expanding-textarea-font-size);

    display: flex;

    align-items: center;

    box-sizing: border-box;
    width: calc(var(--current-cell-width) - 20px);

    padding-left: var(--expanding-textarea-padding);
    padding-right: var(--expanding-textarea-padding);

    border: solid 1px var(--border-color);

    border-radius: var(--border-radius);

    transition: var(--input-transition);
  }

  .m-expanding-input__field-wrapper:hover {
    cursor: text;
    border-color: var(--action-color);
  }

  .m-expanding-input__field-wrapper span {
    text-overflow: ellipsis;
    overflow: hidden;
  }

  .m-input_disabled {
    cursor: not-allowed;
  }

  .m-input_error {
    border-color: var(--error-color);
  }

  .m-expanding-input__grow-wrap {
    word-break: break-all;

    /* easy way to plop the elements on top of each other and have them both sized based on the tallest one's height */
    display: grid;

    height: var(--expanding-textarea-height);
  }

  .m-expanding-input__grow-wrap::after {
    /* This is how textarea text behaves */
    white-space: pre-wrap;

    /* Note the weird space! Needed to prevent jumpy behavior */
    content: attr(data-replicated-value) ' ';

    /* Hidden from view, clicks, and screen readers */
    visibility: hidden;
  }

  .m-expanding-input__grow-wrap > textarea {
    /* Firefox shows scrollbar on growth, you can hide like this. */
    overflow: hidden;

    /* You could leave this, but after a user resizes, then it ruins the auto sizing */
    resize: none;
  }

  .m-expanding-input__grow-wrap > textarea,
  .m-expanding-input__grow-wrap::after {
    font: inherit;

    /* Place on top of each other */
    grid-area: 1 / 1 / 2 / 2;

    padding: var(--expanding-textarea-padding);

    /* Identical styling required!! */
    border: 1px solid var(--border-color);

    border-radius: var(--border-radius);
  }

  .m-expanding-input__grow-wrap textarea {
    min-height: 28px;
    box-shadow: var(--box-shadow);
  }

  .m-expanding-input__grow-wrap textarea:focus {
    border-color: var(--action-color);
    outline: none;
  }

  .popper.m-expanding-input__popper {
    box-shadow: none;
  }
</style>
